import videoManifestoThumbnail from "@/assets/video-manifesto-thumbnail-new.webp";
import ClientesThopen from "@/components/ClientesThopen";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import FaleComEspecialistaForm from "@/components/ui/FaleComEspecialistaForm";
import {
  ArrowRight,
  Award,
  Building2,
  Calculator,
  Check,
  ExternalLink,
  Handshake,
  Leaf,
  MapPin,
  Play,
  Star,
  TrendingUp,
  Users,
  Zap
} from "lucide-react";
import { REGULARIZE_AI_URL, trackRegularizeAiClick } from "@/lib/regularizeAi";
import { useState } from "react";
import { Link } from "react-router-dom";
import AnimatedSection from "../components/AnimatedSection";
import BrazilInteractiveMap from "../components/BrazilInteractiveMap";
import HeroSection from "../components/HeroSection";
import Layout from "../components/Layout";
import WhyThopen from "../components/WhyThopen";
import { useCountAnimation } from "../hooks/useCountAnimation";

const Index = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <Layout>
      <HeroSection />

      {/* Experiência Energética Section */}
      <AnimatedSection delay={100}>
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection delay={200}>
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6 max-w-4xl mx-auto leading-tight">
                  Transformamos a experiência energética com{" "}
                  <span className="bg-gradient-to-r from-thopen-pink to-thopen-yellow bg-clip-text text-transparent">
                    atendimento dedicado
                  </span>{" "}
                  e{" "}
                  <span className="bg-gradient-to-r from-thopen-yellow to-thopen-pink bg-clip-text text-transparent">
                    tecnologia de ponta
                  </span>{" "}
                  para você reduzir custos de energia
                </h2>
              </div>
            </AnimatedSection>

            {/* Map + Statistics Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Map - Left Side */}
              <AnimatedSection delay={400}>
                <BrazilInteractiveMap />
              </AnimatedSection>

              {/* Statistics - Right Side (2x2 Grid) */}
              <AnimatedSection delay={500}>
                <div className="grid grid-cols-2 gap-4 md:gap-6">
                  {[
                    {
                      text: "Ampla capacidade instalada",
                      icon: Zap,
                    },
                    {
                      text: "Milhares de UC's sob gestão",
                      icon: Users,
                    },
                    {
                      text: "Presença nacional",
                      icon: MapPin,
                    },
                    {
                      text: "Amplo portfólio de usinas",
                      icon: Building2,
                    },
                  ].map((stat, index) => (
                    <AnimatedSection
                      key={index}
                      delay={600 + index * 100}
                      direction="up"
                      className="h-full"
                    >
                      <div className="relative text-center p-6 md:p-8 bg-white rounded-3xl border border-gray-100 hover:border-transparent transition-all duration-500 hover:-translate-y-2 group overflow-hidden hover:shadow-[0_20px_50px_-12px_rgba(236,72,153,0.25)] h-full flex flex-col justify-center">
                        {/* Background gradient on hover */}
                        <div className="absolute inset-0 bg-gradient-to-br from-thopen-pink/5 via-transparent to-thopen-yellow/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                        {/* Top accent */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-thopen-pink to-thopen-yellow rounded-b-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />

                        <div className="relative z-10">
                          <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-thopen-pink to-thopen-yellow rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-5 shadow-lg">
                            <stat.icon className="h-6 w-6 md:h-7 md:w-7 text-white" />
                          </div>
                          <div className="text-base md:text-lg font-semibold text-gray-800 group-hover:text-thopen-pink transition-all duration-300">
                            {stat.text}
                          </div>
                        </div>
                      </div>
                    </AnimatedSection>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Value Proposition Section */}
      <AnimatedSection delay={200}>
        <section className="py-20 bg-gradient-to-b from-white to-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection delay={400}>
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4 leading-tight">
                  O modelo tradicional de energia gera custos e incertezas
                </h2>
              </div>
            </AnimatedSection>

            {/* Pain Points */}
            <AnimatedSection delay={600}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
                {[
                  {
                    icon: TrendingUp,
                    label: "Altos custos",
                    color: "from-red-500 to-red-600",
                  },
                  {
                    icon: Star,
                    label: "Falta de transparência",
                    color: "from-orange-500 to-orange-600",
                  },
                  {
                    icon: Calculator,
                    label: "Burocracia",
                    color: "from-amber-500 to-amber-600",
                  },
                  {
                    icon: Award,
                    label: "Falta de controle",
                    color: "from-yellow-500 to-yellow-600",
                  },
                ].map((item, index) => (
                  <AnimatedSection
                    key={index}
                    delay={700 + index * 100}
                    direction="up"
                  >
                    <div className="relative bg-white rounded-3xl p-8 border border-gray-100 hover:border-transparent text-center transition-all duration-500 hover:-translate-y-2 group overflow-hidden hover:shadow-[0_15px_40px_-10px_rgba(239,68,68,0.2)]">
                      {/* Subtle background gradient */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                      />

                      {/* Bottom accent line */}
                      <div
                        className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${item.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center rounded-b-3xl`}
                      />

                      <div className="relative z-10">
                        <div
                          className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}
                        >
                          <item.icon className="h-7 w-7 text-white" />
                        </div>
                        <span className="text-gray-700 font-semibold text-base group-hover:text-gray-900 transition-colors duration-300">
                          {item.label}
                        </span>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={1000}>
              <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-10">
                Essa ainda é a realidade para muitas empresas.
              </p>
            </AnimatedSection>

            {/* Solution */}
            <AnimatedSection delay={1100}>
              <div className="bg-gradient-to-r from-thopen-pink/10 via-white to-thopen-yellow/10 rounded-3xl p-8 md:p-12 border border-gray-100">
                <div className="flex flex-col md:flex-row items-center gap-8">
                  <div className="w-24 h-24 bg-gradient-to-r from-thopen-pink to-thopen-yellow rounded-full flex items-center justify-center flex-shrink-0">
                    <Leaf className="h-12 w-12 text-white" />
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-display font-bold text-gray-900 mb-3">
                      Energia pensada de ponta a ponta
                    </h3>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      De produção a gestão, a Thopen estrutura soluções que eliminam ineficiências e geram economia real para empresas e residências.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </AnimatedSection>

      {/* Renegociação de Débitos (regularize-aí) */}
      <AnimatedSection delay={200}>
        {/* Section branca com painel contido: a seção acima termina em
            `gray-50` e a de baixo (WhyThopen) é branca, então o painel
            flutuante marca a fronteira sem precisar de mais uma faixa cinza.

            Sobre o padding: acima do painel a troca de cor corta o respiro no
            `pt` daqui, enquanto abaixo o branco é contínuo e soma o `pb` daqui
            mais o `pt` do WhyThopen (80px). Por isso o `pt` vale sempre
            80 + `pb`, para o branco ficar igual dos dois lados: 120px no
            mobile e 160px no desktop. */}
        <section className="pt-[120px] pb-10 md:pt-40 md:pb-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-pink-50 to-yellow-50 px-5 py-12 sm:px-8 md:px-12 md:py-16">
              <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-thopen-pink/10 blur-[100px]"
              />
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-thopen-yellow/10 blur-[100px]"
              />

              <div className="relative max-w-3xl mx-auto text-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-thopen-pink/10 text-thopen-pink font-semibold rounded-full text-xs sm:text-sm mb-5 sm:mb-6">
                  <Handshake className="w-4 h-4 shrink-0" />
                  RENEGOCIAÇÃO DE DÉBITOS
                </span>

                {/* Gradiente em uma expressão só. Na referência o headline
                    inteiro era gradiente, o que anula a ênfase. */}
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-gray-900 mb-3 sm:mb-4 text-balance">
                  Regularize suas dívidas em poucos minutos,{" "}
                  <span className="bg-gradient-to-r from-thopen-pink to-thopen-yellow bg-clip-text text-transparent">
                    sem burocracia
                  </span>
                </h2>

                <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-7 sm:mb-8 text-pretty">
                  Negociação rápida, segura e 100% online. Consulte o que está
                  em aberto no seu CPF ou CNPJ e escolha a melhor forma de
                  pagamento.
                </p>

                {/* Botão sólido, não em gradiente: o gradiente já trabalha no
                    headline e no fundo. Sólido faz o botão ler como objeto.

                    `thopen-pink-dark` e não `thopen-pink`: com 18px/600 o texto
                    não conta como "grande" no WCAG, então o piso é 4.5:1 — o
                    rosa puro dá 3.88:1 e reprova; o escuro dá 4.67:1. O hover
                    mexe só em sombra e elevação, para a cor não cair abaixo do
                    limite em nenhum estado. */}
                <a
                  href={REGULARIZE_AI_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackRegularizeAiClick(
                      "Possui débitos? Regularize agora",
                      "secao-renegociacao"
                    )
                  }
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 px-6 sm:px-8 py-4 rounded-xl bg-thopen-pink-dark text-white font-semibold text-base sm:text-lg shadow-lg shadow-thopen-pink/25 outline-none transition-all duration-300 hover:shadow-xl hover:shadow-thopen-pink/40 hover:-translate-y-0.5 active:translate-y-0 focus-visible:ring-2 focus-visible:ring-thopen-pink focus-visible:ring-offset-2"
                >
                  {/* Rótulo sempre em uma linha (`whitespace-nowrap`). O
                      texto completo não cabe na largura do botão no mobile,
                      então lá fica só a ação — a pergunta já está no selo e
                      no título logo acima. O ícone vai dentro do fluxo do
                      texto para nunca descolar da última palavra. */}
                  <span className="whitespace-nowrap">
                    <span className="hidden sm:inline">Possui débitos? </span>
                    Regularize agora
                    <ExternalLink
                      className="inline-block w-5 h-5 ml-2 align-text-bottom"
                      aria-hidden
                    />
                  </span>
                  <span className="sr-only">(abre em nova aba)</span>
                </a>

                {/* Coluna no mobile: em linha os três itens quebravam 2 + 1 e
                    deixavam o último órfão no centro. */}
                <ul className="mt-7 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3 sm:gap-x-6 sm:gap-y-3 text-sm text-gray-600">
                  {["Consulta gratuita", "100% online", "Boletos na hora"].map(
                    (item) => (
                      <li key={item} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-thopen-pink shrink-0" />
                        {item}
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection delay={200}>
        <WhyThopen />
      </AnimatedSection>

      {/* Video Explanation Section */}
      <AnimatedSection delay={400}>
        <section className="relative py-20 bg-gray-900 text-white overflow-hidden">
          {/* Large Geometric Element - Behind the video - Hidden on mobile */}
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/4 w-[800px] h-[600px] pointer-events-none hidden lg:block">
            <img
              src="/lovable-uploads/b73bf13d-a997-48c7-9c4d-0c544a1a1e8d.webp"
              alt=""
              className="w-full h-full object-contain"
              width={900}
              height={432}
            />
          </div>

          {/* Large Geometric Element for mobile - Below video box */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-[400px] h-[300px] pointer-events-none lg:hidden z-0 bottom-16">
            <img
              src="/lovable-uploads/b73bf13d-a997-48c7-9c4d-0c544a1a1e8d.webp"
              alt=""
              className="w-full h-full object-contain opacity-30"
              width={900}
              height={432}
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <AnimatedSection direction="left" delay={600}>
                <div>
                  <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
                    A Thopen é para{" "}
                    <span className="bg-gradient-to-r from-thopen-pink to-thopen-yellow bg-clip-text text-transparent">
                      sua casa
                    </span>{" "}
                    e para o{" "}
                    <span className="bg-gradient-to-r from-thopen-pink to-thopen-yellow bg-clip-text text-transparent">
                      seu negócio.
                    </span>
                  </h2>
                  <p className="text-lg md:text-xl mb-8 text-white/80">
                    Hoje, já temos grandes empresas em nosso portfólio. Conheça
                    quem já confia na nossa economia.
                  </p>

                  <AnimatedSection delay={800}>
                    <Link
                      to="/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia"
                      className="inline-flex items-center bg-gradient-to-r from-thopen-pink to-thopen-yellow text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all duration-200 space-x-2 hover:scale-105 transform"
                    >
                      <Calculator className="h-5 w-5" />
                      <span>
                        Também quero economizar até 15% na minha fatura
                      </span>
                    </Link>
                  </AnimatedSection>
                </div>
              </AnimatedSection>

              {/* Video */}
              <AnimatedSection direction="right" delay={600}>
                <div className="relative">
                  <div
                    className="relative bg-gray-800 rounded-2xl overflow-hidden shadow-2xl group cursor-pointer"
                    onClick={() => setIsVideoOpen(true)}
                  >
                    <div className="aspect-video relative">
                      <img
                        src={videoManifestoThumbnail}
                        alt="Economize até 15% na conta de energia"
                        className="w-full h-full object-cover rounded-t-2xl"
                        width={900}
                        height={626}
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-t-2xl">
                        <div className="text-center">
                          <div className="w-20 h-20 bg-gradient-to-r from-thopen-pink/10 to-thopen-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform cursor-pointer group-hover:animate-pulse">
                            <Play className="h-10 w-10 text-white ml-1" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-white text-sm">
                            E saiba mais sobre como funciona a Geração
                            Distribuída{" "}
                          </h3>
                          <p className="text-sm text-gray-400">
                            3:45 min • Explicação completa
                          </p>
                        </div>
                        <div className="text-thopen-yellow text-sm font-medium hover:text-thopen-pink transition-colors duration-200">
                          ▶ Assistir agora
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* Video Modal */}
              <Dialog open={isVideoOpen} onOpenChange={setIsVideoOpen}>
                <DialogContent className="max-w-4xl p-0 bg-black border-none">
                  <div className="aspect-video w-full">
                    <iframe
                      width="100%"
                      height="100%"
                      src={
                        isVideoOpen
                          ? "https://www.youtube.com/embed/x36_3O6HsSU?autoplay=1"
                          : ""
                      }
                      title="Geração Distribuída - Thopen Energy"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="rounded-lg"
                    />
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </section>
      </AnimatedSection>

      <AnimatedSection delay={200}>
        <ClientesThopen />
      </AnimatedSection>

      {/* Produtos e Soluções Section */}
      <AnimatedSection delay={200}>
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection delay={400}>
              <div className="text-center mb-16">
                <span className="text-thopen-pink font-semibold text-sm uppercase tracking-wider">
                  Nossos Produtos e Soluções
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mt-4">
                  Um ecossistema completo de{" "}
                  <span className="bg-gradient-to-r from-thopen-pink to-thopen-yellow bg-clip-text text-transparent">
                    energia inteligente
                  </span>
                </h2>
              </div>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Mercado Livre de Energia (ACL)",
                  description:
                    "Para empresas que buscam previsibilidade, liberdade e controle total sobre seus custos de energia. A Thopen conecta você diretamente às melhores oportunidades do mercado, com inteligência estratégica e suporte especializado.",
                  icon: TrendingUp,
                  link: "/solucoes/nossos-produtos-e-servicos/mercado-livre-de-energia",
                  tags: [
                    {
                      label: "Para sua empresa",
                      color: "border-thopen-blue text-thopen-blue",
                    },
                  ],
                },
                {
                  title: "Geração Distribuída (GD)",
                  description:
                    "Energia limpa e local, com economia e sustentabilidade. Atuamos em toda a cadeia da GD, desde a gestão dos ativos até a conexão com a rede, garantindo simplicidade, eficiência e impacto ambiental positivo.",
                  icon: Zap,
                  link: "/solucoes/nossos-produtos-e-servicos/geracao-distribuida/geracao-remota",
                  tags: [
                    {
                      label: "Para sua empresa",
                      color: "border-thopen-blue text-thopen-blue",
                    },
                    {
                      label: "Para sua casa",
                      color: "border-emerald-500 text-emerald-500",
                    },
                  ],
                },
                {
                  title: "Diagnóstico e Auditoria de Faturas",
                  description:
                    "Identificamos oportunidades ocultas, eliminamos ineficiências e corrigimos distorções em contratos e tarifas. Uma solução essencial para quem deseja clareza, conformidade e economia real.",
                  icon: Calculator,
                  link: "/solucoes/gestao-e-eficiencia-energetica/diagnostico-de-contas-de-energia",
                  tags: [
                    {
                      label: "Para sua empresa",
                      color: "border-thopen-blue text-thopen-blue",
                    },
                    {
                      label: "Para sua casa",
                      color: "border-emerald-500 text-emerald-500",
                    },
                  ],
                },
                {
                  title: "Ressarcimento",
                  description:
                    "Recuperamos valores pagos indevidamente em faturas de energia, com base em análises técnicas e jurídicas especializadas. Mais que economia: justiça energética.",
                  icon: Award,
                  link: "/solucoes/gestao-e-eficiencia-energetica/ressarcimento-de-valores-indevidos",
                  tags: [
                    {
                      label: "Para sua empresa",
                      color: "border-thopen-blue text-thopen-blue",
                    },
                    {
                      label: "Para sua casa",
                      color: "border-emerald-500 text-emerald-500",
                    },
                  ],
                },
                {
                  title: "Gestão de Faturas",
                  description:
                    "Fazemos a gestão da sua fatura. Centralizamos, organizamos e otimizamos todas as suas faturas, com tecnologia e inteligência. Garantimos previsibilidade, compliance e informações estratégicas para a sua gestão.",
                  icon: Users,
                  link: "/solucoes/gestao-e-eficiencia-energetica/gestao-completa-de-faturas",
                  tags: [
                    {
                      label: "Para sua empresa",
                      color: "border-thopen-blue text-thopen-blue",
                    },
                  ],
                },
                {
                  title: "Plataforma Backoffice",
                  description:
                    "O coração digital da sua operação energética. Automatizamos processos, integramos dados e simplificamos a gestão de ponta a ponta, seja no ACL ou na GD.",
                  icon: Building2,
                  link: "/solucoes/nossos-produtos-e-servicos/geracao-distribuida/gestao-backoffice",
                  tags: [
                    {
                      label: "Para sua empresa",
                      color: "border-thopen-blue text-thopen-blue",
                    },
                  ],
                },
              ].map((product, index) => (
                <AnimatedSection
                  key={index}
                  delay={500 + index * 100}
                  direction="up"
                >
                  <div className="relative bg-gradient-to-br from-gray-50 via-white to-rose-50/30 rounded-3xl p-8 h-full transition-all duration-500 hover:-translate-y-2 group overflow-hidden hover:shadow-xl flex flex-col">
                    {/* Subtle decorative gradient blob */}
                    <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-thopen-pink/10 to-thopen-yellow/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

                    <div className="relative z-10 flex flex-col flex-grow">
                      {/* Icon with gradient background */}
                      <div className="w-14 h-14 bg-gradient-to-br from-thopen-pink to-thopen-yellow rounded-xl flex items-center justify-center mb-6 shadow-md">
                        <product.icon className="h-7 w-7 text-white" />
                      </div>

                      {/* Title */}
                      <h3 className="text-xl font-display font-bold text-gray-900 mb-3 leading-tight">
                        {product.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-500 leading-relaxed text-sm mb-4">
                        {product.description}
                      </p>

                      {/* Tags - moved below description */}
                      <div className="flex flex-wrap gap-2 mt-auto mb-4">
                        {product.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className={`${tag.color} border text-xs font-semibold px-2.5 py-1 rounded-full bg-transparent`}
                          >
                            {tag.label}
                          </span>
                        ))}
                      </div>

                      {/* Secondary Button */}
                      <Link
                        to={product.link}
                        className="mt-6 inline-flex items-center justify-center border-2 border-thopen-pink text-thopen-pink px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-thopen-pink hover:text-white transition-all duration-200 group/btn"
                      >
                        <span>Saiba mais</span>
                        <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* FAQ Section */}
      <AnimatedSection delay={200}>
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection delay={400}>
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
                  Dúvidas frequentes
                </h2>
                <p className="text-xl text-gray-600">
                  Tire suas dúvidas sobre energia livre e economia na conta de
                  energia
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <Accordion type="single" collapsible className="w-full space-y-4">
                {[
                  {
                    value: "item-1",
                    question: "O que é a Thopen?",
                    answer:
                      "A Thopen é uma plataforma de energia que reúne produção própria, gestão e soluções inteligentes para reduzir custos e simplificar a relação com a energia. Atuamos com modelos mais eficientes, transparentes e sustentáveis para residências e empresas.",
                  },
                  {
                    value: "item-2",
                    question: "Como a Thopen me ajuda a economizar na conta de energia?",
                    answer:
                      "A economia acontece por meio de modelos mais eficientes de produção e gestão da energia. A Thopen produz energia limpa em usinas próprias e estrutura soluções que reduzem custos e trazem mais previsibilidade, sem exigir mudanças na sua operação.",
                  },
                  {
                    value: "item-3",
                    question: "Preciso instalar algo ou fazer obras?",
                    answer:
                      "Não. As soluções da Thopen não exigem instalação de equipamentos, mudanças na fiação ou obras. A energia continua chegando normalmente pela rede da distribuidora local.",
                  },
                  {
                    value: "item-4",
                    question: "A Thopen substitui minha distribuidora de energia?",
                    answer:
                      "Não. A distribuidora continua responsável pela entrega da energia, manutenção da rede e leitura do consumo. A Thopen atua na forma como a energia é produzida, compensada e gerida, tornando o modelo mais eficiente.",
                  },
                  {
                    value: "item-5",
                    question: "Para quem as soluções da Thopen são indicadas?",
                    answer:
                      "As soluções da Thopen atendem residências e empresas que buscam reduzir custos e ter mais clareza e controle sobre a energia. A indicação depende do perfil de consumo, da localização e do tipo de solução mais adequada para cada caso.",
                  },
                  {
                    value: "item-6",
                    question: "A Thopen atende todo o Brasil?",
                    answer:
                      "Atendemos diversas regiões do país. A disponibilidade das soluções pode variar conforme a área de atuação das usinas e das distribuidoras locais. Sempre realizamos uma análise prévia para confirmar o atendimento.",
                  },
                  {
                    value: "item-7",
                    question: "A economia é garantida?",
                    answer:
                      "A economia depende do perfil de consumo e das regras aplicáveis a cada modelo. Antes da contratação, a Thopen realiza uma análise para confirmar se a solução é indicada e se há potencial real de economia.",
                  },
                  {
                    value: "item-8",
                    question: "Que tipo de energia a Thopen produz?",
                    answer:
                      "A Thopen produz energia limpa e renovável em usinas próprias, com destaque para fontes solares e de biogás, contribuindo para uma matriz energética mais sustentável e eficiente.",
                  },
                  {
                    value: "item-9",
                    question: "A Thopen é regulamentada e segura?",
                    answer:
                      "Sim. As soluções da Thopen seguem as regras do setor elétrico brasileiro e operam dentro dos modelos regulados. O fornecimento de energia continua sendo feito pela distribuidora local, garantindo segurança e continuidade.",
                  },
                ].map((item, index) => (
                  <AnimatedSection key={item.value} delay={800 + index * 100}>
                    <AccordionItem
                      value={item.value}
                      className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-300"
                    >
                      <AccordionTrigger className="px-6 py-4 text-left font-semibold text-gray-900 hover:text-thopen-pink transition-colors duration-200">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4 text-gray-700">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </AnimatedSection>
                ))}
              </Accordion>
            </AnimatedSection>

            <AnimatedSection delay={1400}>
              <div className="text-center mt-12">
                <Link
                  to="/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia"
                  className="inline-flex items-center bg-gradient-to-r from-thopen-pink to-thopen-yellow text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-200 space-x-2 transform hover:scale-105"
                >
                  <Calculator className="h-5 w-5" />
                  <span>Quero parar de pagar mais caro</span>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </AnimatedSection>

      {/* Multiple CTAs Section with Brand Gradient */}
      <AnimatedSection delay={200}>
        <section
          className="py-20 relative bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('/lovable-uploads/f3e9cb01-dc1d-480a-a318-10503122eaf9.webp')",
          }}
        >
          {/* Removed dark overlay to show image without filters */}
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <AnimatedSection delay={400}>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
                <span className="bg-gradient-to-r from-pink-500 via-red-500 to-orange-400 bg-clip-text text-transparent">
                  Pare de pagar mais caro por energia
                </span>
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <p className="text-xl text-pink-100 mb-8 max-w-2xl mx-auto">
                Junte-se a consumidores de todo o Brasil que já contam com a
                Thopen para tornar sua gestão de energia mais eficiente.
                Calcule sua economia na conta de luz agora mesmo.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Link
                  to="/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia"
                  className="bg-white text-thopen-pink px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2 transform hover:scale-105"
                >
                  <Calculator className="h-5 w-5" />
                  <span>Calcular economia agora</span>
                </Link>
                {/* <FaleComEspecialistaForm
                  buttonClassName='border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-thopen-pink transition-all duration-200 hover:scale-105 transform'
                /> */}
              </div>
            </AnimatedSection>

            <AnimatedSection delay={1000}>
              <p className="text-sm text-pink-100">
                Sem compromisso • Simulação gratuita • Atendimento personalizado
              </p>
            </AnimatedSection>
          </div>
        </section>
      </AnimatedSection>
    </Layout>
  );
};

// New component for the energy statistic with custom layout
const EnergyStatisticCard = ({
  icon: Icon,
  value,
  label,
  suffix = "",
  sublabel,
  delay = 0,
}: {
  icon: any;
  value: number;
  label: string;
  suffix?: string;
  sublabel: string;
  delay?: number;
}) => {
  const { count, elementRef } = useCountAnimation(value, 2000);
  return (
    <AnimatedSection delay={delay}>
      <div
        ref={elementRef}
        className="text-left group hover:scale-105 transition-transform duration-300"
      >
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:bg-white/30 transition-colors duration-300">
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="mb-1">
          <div className="text-pink-100 font-medium text-sm">{label}</div>
          <div className="text-2xl font-bold">
            {count}
            {suffix}
          </div>
        </div>
        <div className="text-pink-100 font-medium text-sm">{sublabel}</div>
      </div>
    </AnimatedSection>
  );
};

// Updated StatisticCard component with equivalences
const StatisticCard = ({
  icon: Icon,
  value,
  prefix = "",
  suffix = "",
  label,
  equivalence = "",
  delay = 0,
  format = "number",
}: {
  icon: any;
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  equivalence?: string;
  delay?: number;
  format?: "number" | "k";
}) => {
  const { count, elementRef } = useCountAnimation(value, 2000);
  const formatNumber = (num: number) => {
    return `${prefix}${num}${suffix}`;
  };
  return (
    <AnimatedSection delay={delay}>
      <div
        ref={elementRef}
        className="text-left group hover:scale-105 transition-transform duration-300"
      >
        <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3 group-hover:bg-white/30 transition-colors duration-300">
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="text-2xl font-bold mb-1">{formatNumber(count)}</div>
        <div className="text-pink-100 font-medium text-sm mb-1">{label}</div>
        {equivalence}
      </div>
    </AnimatedSection>
  );
};
export default Index;
